import {
  Autocomplete,
  CircularProgress,
  ListItem,
  ListItemText,
  TextField,
} from "@mui/material";
import { useRef, useState } from "react";

import { INPUT_SX } from "./form-text-field";

export interface AddressAutofill {
  line1: string;
  postalCode: string;
  city: string;
  country: string;
}

interface PhotonFeatureProperties {
  name?: string;
  housenumber?: string;
  street?: string;
  postcode?: string;
  city?: string;
  town?: string;
  village?: string;
  county?: string;
  state?: string;
  country?: string;
  countrycode?: string;
}

interface PhotonFeature {
  properties: PhotonFeatureProperties;
}

interface PhotonResponse {
  features: PhotonFeature[];
}

interface Option {
  label: string;
  fill: AddressAutofill;
}

export interface AddressAutocompleteProps {
  value: string;
  label: string;
  onChange: (value: string) => void;
  onSelect: (fill: AddressAutofill) => void;
  required?: boolean;
}

function mapFeatureToOption(f: PhotonFeature): Option {
  const p = f.properties;
  const street = p.street ?? p.name ?? "";
  const line1 = [street, p.housenumber].filter(Boolean).join(" ").trim();
  const city = p.city ?? p.town ?? p.village ?? p.county ?? p.state ?? "";
  const country = p.countrycode ?? "";
  const postalCode = p.postcode ?? "";
  const label = [line1, postalCode, city, p.country].filter(Boolean).join(", ");
  return { label, fill: { line1, postalCode, city, country } };
}

export function AddressAutocomplete({
  value,
  label,
  onChange,
  onSelect,
  required,
}: AddressAutocompleteProps) {
  const [options, setOptions] = useState<Option[]>([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState(value);
  const [prevExternalValue, setPrevExternalValue] = useState(value);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestIdRef = useRef(0);

  // Sync external resets (e.g. form cleared or address loaded) using React's
  // getDerivedStateFromProps pattern — no ref access during render.
  if (prevExternalValue !== value) {
    setPrevExternalValue(value);
    setInputValue(value);
  }

  const fetchSuggestions = (query: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (query.length < 3) {
      setOptions([]);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      const requestId = ++requestIdRef.current;
      setLoading(true);
      try {
        const res = await fetch(
          `https://photon.komoot.io/api/?q=${encodeURIComponent(query)}&limit=5`,
          { cache: "no-store" }
        );
        if (!res.ok) return;
        const data: PhotonResponse = await res.json();
        if (requestIdRef.current !== requestId) return;
        setOptions(data.features.map(mapFeatureToOption));
      } catch {
        // silently ignore network errors — user can still type manually
      } finally {
        if (requestIdRef.current === requestId) setLoading(false);
      }
    }, 500);
  };

  return (
    <Autocomplete
      freeSolo
      options={options}
      loading={loading}
      inputValue={inputValue}
      onInputChange={(_, newValue, reason) => {
        if (reason === "input") {
          setInputValue(newValue);
          onChange(newValue);
          fetchSuggestions(newValue);
        } else if (reason === "clear") {
          setInputValue("");
          onChange("");
          setOptions([]);
        } else if (reason === "selectOption") {
          // newValue is already fill.line1 because getOptionLabel returns fill.line1
          setInputValue(newValue);
          onChange(newValue);
        }
      }}
      onChange={(_, newValue) => {
        if (newValue && typeof newValue !== "string") {
          onSelect(newValue.fill);
        }
      }}
      // disable client-side filtering — results come pre-filtered from Photon
      filterOptions={(x) => x}
      // display only street+number in the input after selection
      getOptionLabel={(opt) => (typeof opt === "string" ? opt : opt.fill.line1)}
      renderOption={(props, opt) => (
        <ListItem {...props} key={opt.label}>
          <ListItemText
            primary={opt.fill.line1}
            secondary={[
              opt.fill.postalCode,
              opt.fill.city,
              opt.label.split(", ").at(-1),
            ]
              .filter(Boolean)
              .join(", ")}
          />
        </ListItem>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          required={required}
          label={label}
          size="small"
          sx={INPUT_SX}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? (
                  <CircularProgress size={14} sx={{ mr: 0.5 }} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
